import { Suspense, useEffect, useLayoutEffect } from "react";
import { BrowserRouter } from "react-router-dom";
import { QueryClient, QueryClientProvider, MutationCache } from "@tanstack/react-query";
import { toast } from "@/shared/hooks/use-toast";
import { resolveApiErrorMessage, apiErrorTitle } from "@/shared/utils/apiErrorMessage";
import { ThemeProvider } from "@/shared/hooks/use-theme";
import { AuthProvider } from "@/features/Auth/state/AuthContext";
import { SuperAdminOrgProvider } from "@/shared/contexts/SuperAdminOrgContext";
import { FeatureFlagsProvider } from "@/shared/contexts/FeatureFlagsContext";
import { WorkspaceProvider } from "@/features/Workspaces/state/WorkspaceContext";
import { UploadProvider } from "@/features/Knowledge/state/UploadContext";
import { AvatarProvider } from "@/features/Profile/state/AvatarContext";
import { Toaster } from "@/shared/components/ui/toaster";
import { Toaster as Sonner } from "@/shared/components/ui/sonner";
import { TooltipProvider } from "@/shared/components/ui/tooltip";
import { LoadingSpinner } from "@/shared/components/ui/loading-spinner";
import { ErrorBoundary } from "@/shared/components/error/ErrorBoundary";
import { AppRoutes } from "@/app/router/AppRoutes";
import { URLTracker } from "@/app/components/URLTracker";
import "@/app/i18n/config";
import { RecoilRoot } from 'recoil';
import { disableLogs } from "@/shared/utils/disableLogs";
import { useVersion } from "@/shared/hooks/api/useVersion";
import { useGoogleTagManager } from "@/shared/hooks/useGoogleTagManager";
import { TourProvider } from "@/features/Tour";

disableLogs(false, true);

const queryClient = new QueryClient({
  // Toast amigável e localizado para qualquer mutation que NÃO trate o próprio
  // erro. Pulamos quando a mutation define `onError` (hook-level) ou opta por
  // `meta.suppressErrorToast`, evitando toast duplicado. 401 (refresh/redirect)
  // e 429 (rate-limit) já são tratados no fetchApi.
  mutationCache: new MutationCache({
    onError: (error, _vars, _ctx, mutation) => {
      if (mutation.options.onError) return;
      if (mutation.meta?.suppressErrorToast) return;
      const status = (error as { status?: number; response?: { status?: number } })?.status
        ?? (error as { response?: { status?: number } })?.response?.status;
      if (status === 401 || status === 429) return;
      toast({
        title: apiErrorTitle(),
        description: resolveApiErrorMessage(error),
        variant: "destructive",
      });
    },
  }),
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000,
      gcTime: 30 * 60 * 1000, // Updated from cacheTime to gcTime
      retry: 2,
      refetchOnWindowFocus: false,
      refetchOnReconnect: 'always'
    },
    mutations: {
      retry: 1
    }
  }
});

const AppContent = () => {
  // Verificar se estamos em uma página pública
  const isPublicPage = window.location.pathname.startsWith('/login') ||
                       window.location.pathname.startsWith('/auth') ||
                       window.location.pathname.startsWith('/oauth') ||
                       window.location.pathname.startsWith('/register') ||
                       window.location.pathname.startsWith('/signup') ||
                       window.location.pathname.startsWith('/recover-password') ||
                       window.location.pathname.startsWith('/unsubscribe') ||
                       window.location.pathname.startsWith('/widget') ||
                       window.location.pathname.startsWith('/communicator');
  
  // Só buscar versão se não estivermos em página pública (incluindo OAuth callback)
  const { data: versionInfo } = useVersion({ enabled: !isPublicPage });
  
  // Initialize Google Tag Manager
  useGoogleTagManager();

  useLayoutEffect(() => {
    if (versionInfo?.environment === 'development') {
      disableLogs(true, false);
    }
  }, [versionInfo]);

  useEffect(() => {
    // Save current URL immediately on app initialization to preserve it through auth flow
    const currentPath = window.location.pathname + window.location.search;
    const isAuthPage = currentPath.startsWith('/login') ||
      currentPath.startsWith('/auth') ||
      currentPath.startsWith('/register') ||
      currentPath.startsWith('/widget') ||
      currentPath.startsWith('/communicator');

    // Only save non-auth pages as redirect target
    if (!isAuthPage && currentPath !== '/') {
      sessionStorage.setItem('prodgy_redirect_after_login', currentPath);
    }
  }, []);

  useEffect(() => {
    document.title = 'Prodgy | The AI Company OS';
  }, []);

  return (
    <BrowserRouter>
      <URLTracker />
      <ErrorBoundary>
        <ThemeProvider defaultTheme="light">
          <AuthProvider>
            <SuperAdminOrgProvider>
            <FeatureFlagsProvider>
            <WorkspaceProvider>
              <TourProvider>
                <UploadProvider>
                  <AvatarProvider>
                    <TooltipProvider>
                      <Suspense fallback={<LoadingSpinner />}>
                        <AppRoutes />
                      </Suspense>
                      <Toaster />
                      <Sonner />
                    </TooltipProvider>
                  </AvatarProvider>
                </UploadProvider>
              </TourProvider>
            </WorkspaceProvider>
            </FeatureFlagsProvider>
            </SuperAdminOrgProvider>
          </AuthProvider>
        </ThemeProvider>
      </ErrorBoundary>
    </BrowserRouter>
  );
};

const App = () => {
  return (
    <RecoilRoot>
      <QueryClientProvider client={queryClient}>
        <AppContent />
      </QueryClientProvider>
    </RecoilRoot>
  );
};

export default App;
